import React from 'react';
import { 
  Phone, MessageCircle, MapPin, Star, Download, Play,
  ChevronRight, Mail, User, Building2, IndianRupee, Check
} from 'lucide-react';

// Microsite Preview Component - Renders the actual landing page preview
const MicrositePreview = ({ microsite, project }) => {
  const { sections = [], settings = {} } = microsite;
  const primaryColor = settings.primaryColor || '#0f172a';
  const accentColor = settings.accentColor || '#b45309';

  const visibleSections = sections.filter(s => s.is_visible !== false);

  const renderSection = (section) => {
    const { type, content = {} } = section;

    switch (type) {
      case 'hero':
        return (
          <div 
            key={section.id}
            className="relative min-h-[400px] flex items-center justify-center text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {content.backgroundImage && (
              <img 
                src={content.backgroundImage} 
                alt="Hero" 
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
            )}
            <div className="relative z-10 text-center px-6 py-12">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">
                {content.title || project?.name || 'Project Name'}
              </h1>
              <p className="text-lg md:text-xl opacity-90 mb-6">
                {content.subtitle || project?.location || 'Premium Living Redefined'}
              </p>
              <button 
                className="px-8 py-3 rounded-full font-semibold text-lg"
                style={{ backgroundColor: accentColor }}
              >
                Enquire Now
              </button>
            </div>
          </div>
        );

      case 'about':
        return (
          <div key={section.id} className="py-12 px-6 bg-white">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-6" style={{ color: primaryColor }}>
                {content.title || 'About the Project'}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {content.text || project?.description || 'Experience luxury living at its finest with world-class amenities, prime location, and exceptional design. This project offers the perfect blend of comfort and elegance for modern families.'}
              </p>
            </div>
          </div>
        );

      case 'highlights':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: `${primaryColor}08` }}>
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Key Highlights'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {(content.items || ['Premium Location', 'World-class Amenities', 'RERA Approved', 'Ready to Move']).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white p-4 rounded-lg shadow-sm">
                  <Star className="h-5 w-5 shrink-0" style={{ color: accentColor }} />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div key={section.id} className="py-12 px-6 bg-white">
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Gallery'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Building2 className="h-8 w-8" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'video':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: primaryColor }}>
            <h2 className="text-2xl font-bold text-center mb-8 text-white">
              {content.title || 'Virtual Tour'}
            </h2>
            <div className="max-w-3xl mx-auto aspect-video bg-black/30 rounded-xl flex items-center justify-center">
              <button className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                <Play className="h-8 w-8 text-white ml-1" />
              </button>
            </div>
          </div>
        );

      case 'amenities':
        return (
          <div key={section.id} className="py-12 px-6 bg-white">
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Amenities'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {(content.items || ['Swimming Pool', 'Gymnasium', 'Clubhouse', 'Children Play Area', 'Landscaped Gardens', 'Jogging Track', '24/7 Security', 'Power Backup']).map((item, idx) => (
                <div key={idx} className="text-center p-4">
                  <div 
                    className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
                    style={{ backgroundColor: `${accentColor}20` }}
                  >
                    <Check className="h-6 w-6" style={{ color: accentColor }} />
                  </div>
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'floor_plans':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: `${primaryColor}08` }}>
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Floor Plans'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {(project?.bhk_options || ['2 BHK', '3 BHK', '4 BHK']).map((bhk, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm text-center">
                  <h3 className="text-xl font-bold mb-2" style={{ color: primaryColor }}>{bhk}</h3>
                  <p className="text-gray-500 text-sm mb-4">{900 + idx * 200} - {1100 + idx * 200} sq.ft</p>
                  <div className="aspect-[4/3] bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-gray-300" />
                  </div>
                  <button 
                    className="w-full py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: accentColor }}
                  >
                    View Plan
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div key={section.id} className="py-12 px-6 bg-white">
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Pricing'}
            </h2>
            <div className="max-w-2xl mx-auto">
              <div className="border rounded-xl overflow-hidden">
                <div className="grid grid-cols-3 text-sm font-semibold text-white p-4" style={{ backgroundColor: primaryColor }}>
                  <div>Unit Type</div>
                  <div>Size (sq.ft)</div>
                  <div>Price</div>
                </div>
                {(project?.bhk_options || ['2 BHK', '3 BHK', '4 BHK']).map((bhk, idx) => (
                  <div key={idx} className="grid grid-cols-3 text-sm p-4 border-t">
                    <div className="font-medium">{bhk}</div>
                    <div>{900 + idx * 200} - {1100 + idx * 200}</div>
                    <div className="font-semibold" style={{ color: accentColor }}>
                      ₹{(0.85 + idx * 0.5).toFixed(2)} Cr*
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">*Price may vary. Contact for exact pricing.</p>
            </div>
          </div>
        );

      case 'nearby':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: `${primaryColor}08` }}>
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Nearby Attractions'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { category: 'Education', items: ['International School - 2 km', 'Engineering College - 5 km'] },
                { category: 'Healthcare', items: ['Multi-specialty Hospital - 3 km', 'Clinic - 1 km'] },
                { category: 'Shopping', items: ['Mall - 4 km', 'Supermarket - 1 km'] },
              ].map((cat, idx) => (
                <div key={idx} className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold mb-3" style={{ color: primaryColor }}>{cat.category}</h3>
                  <ul className="space-y-2">
                    {cat.items.map((item, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                        <MapPin className="h-4 w-4 shrink-0" style={{ color: accentColor }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'location':
        return (
          <div key={section.id} className="py-12 px-6 bg-white">
            <h2 className="text-2xl font-bold text-center mb-8" style={{ color: primaryColor }}>
              {content.title || 'Location'}
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="aspect-video bg-gray-200 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500">Google Maps Embed</p>
                  <p className="text-sm text-gray-400 mt-1">{content.address || project?.location || 'Location Address'}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'testimonials':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: primaryColor }}>
            <h2 className="text-2xl font-bold text-center mb-8 text-white">
              {content.title || 'What Our Customers Say'}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                { name: 'Rahul Sharma', text: 'Excellent project with great amenities. The location is perfect and the builder is very professional.' },
                { name: 'Priya Patel', text: 'We loved the floor plan and the quality of construction. Highly recommended!' },
              ].map((review, idx) => (
                <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <p className="text-white/90 mb-4 italic">"{review.text}"</p>
                  <p className="text-white font-medium">- {review.name}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'lead_form':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: `${accentColor}15` }}>
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-center mb-2" style={{ color: primaryColor }}>
                {content.title || 'Enquire Now'}
              </h2>
              <p className="text-gray-500 text-center mb-6 text-sm">
                {content.subtitle || 'Get exclusive offers and pricing details'}
              </p>
              <div className="space-y-4">
                <div>
                  <input 
                    type="text" 
                    placeholder="Your Name" 
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <input 
                    type="tel" 
                    placeholder="Phone Number" 
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    placeholder="Email Address" 
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:border-amber-500"
                  />
                </div>
                <button 
                  className="w-full py-3 rounded-lg text-white font-semibold"
                  style={{ backgroundColor: accentColor }}
                >
                  Submit Enquiry
                </button>
              </div>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div key={section.id} className="py-12 px-6" style={{ backgroundColor: primaryColor }}>
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-2xl font-bold mb-6">{content.title || 'Contact Us'}</h2>
              <div className="flex flex-wrap justify-center gap-8">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5" style={{ color: accentColor }} />
                  <span>{content.phone || settings.phoneNumber || '+91 98765 43210'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5" style={{ color: accentColor }} />
                  <span>{content.email || 'info@project.com'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5" style={{ color: accentColor }} />
                  <span>{content.address || project?.location || 'Project Location'}</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'brochure':
        return (
          <div key={section.id} className="py-8 px-6 bg-white border-t">
            <div className="max-w-md mx-auto text-center">
              <button 
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold"
                style={{ backgroundColor: accentColor, color: 'white' }}
              >
                <Download className="h-5 w-5" />
                {content.buttonText || 'Download Brochure'}
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Floating WhatsApp Button
  const hasWhatsApp = sections.some(s => s.type === 'whatsapp' && s.is_visible !== false);
  // Floating Call Button
  const hasCallNow = sections.some(s => s.type === 'call_now' && s.is_visible !== false);

  return (
    <div className="bg-white min-h-full">
      {/* Render all visible sections */}
      {visibleSections.map(renderSection)}

      {/* Footer */}
      <div className="py-6 px-6 text-center text-sm text-gray-500 border-t">
        <p>© 2026 {project?.name || 'Project Name'}. All rights reserved.</p>
        {settings.showPartnerInfo && (
          <p className="mt-1">Powered by effyRealty</p>
        )}
      </div>

      {/* Floating Buttons */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3">
        {hasWhatsApp && (
          <button 
            className="h-14 w-14 rounded-full bg-green-500 text-white shadow-lg flex items-center justify-center hover:bg-green-600 transition-colors"
          >
            <MessageCircle className="h-7 w-7" />
          </button>
        )}
        {hasCallNow && (
          <button 
            className="h-14 w-14 rounded-full text-white shadow-lg flex items-center justify-center hover:opacity-90 transition-colors"
            style={{ backgroundColor: accentColor }}
          >
            <Phone className="h-7 w-7" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MicrositePreview;
